<template>
  <v-container fluid class="reset-container">
    <v-row justify="center">
      <v-col cols="12" sm="10" md="8" lg="6">
        <v-card class="text-center pa-6">
          <v-card-title class="text-h4 justify-center">
            System Reset
          </v-card-title>
          
          <v-card-subtitle class="text-subtitle-1 red--text mt-2">
            WARNING: Don't make a habit of this
          </v-card-subtitle>
          
          <v-card-text class="text-body-1">
            <p>This will reset the api server. This is an emergency function that should only be used when necessary.</p>
            <p>You will need to confirm this action by entering a verification phrase.</p>
          </v-card-text>
          
          <v-card-actions class="justify-center pa-6">
            <v-btn
              x-large
              color="error"
              class="px-12 py-6"
              :loading="loading"
              @click="showConfirmDialog"
            >
              <v-icon left size="24">mdi-delete-forever</v-icon>
              <span class="text-h5">RESET SYSTEM</span>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
    
    <!-- Confirmation Dialog -->
    <v-dialog v-model="dialogVisible" max-width="500">
      <v-card>
        <v-card-title class="text-h5 red--text">
          Confirm System Reset
        </v-card-title>
        
        <v-card-text>
          <p>To confirm reset, please type the phrase: <strong>"{{ confirmPhrase }}"</strong></p>
          <v-text-field
            v-model="userInput"
            label="Confirmation Phrase"
            outlined
            dense
            :rules="[rules.required, rules.match]"
            :error="!!error"
            :error-messages="error"
            autofocus
            @keydown.enter="confirmReset"
          ></v-text-field>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="grey darken-1"
            text
            @click="cancelReset"
          >
            Cancel
          </v-btn>
          <v-btn
            color="error"
            :disabled="userInput !== confirmPhrase"
            :loading="loading"
            @click="confirmReset"
          >
            Confirm Reset
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Result Dialog -->
    <v-dialog v-model="resultDialogVisible" max-width="500">
      <v-card>
        <v-card-title :class="resetSuccess ? 'success--text' : 'error--text'">
          {{ resetSuccess ? 'Reset Complete' : 'Reset Failed' }}
        </v-card-title>
        
        <v-card-text>
          {{ resetMessage }}
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            text
            @click="resultDialogVisible = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import api from '@/api';

export default {
  name: 'Reset',
  data() {
    return {
      dialogVisible: false,
      resultDialogVisible: false,
      loading: false,
      confirmPhrase: 'do not push the red button',
      userInput: '',
      error: '',
      resetSuccess: false,
      resetMessage: '',
      rules: {
        required: v => !!v || 'Confirmation phrase is required',
        match: v => v === this.confirmPhrase || 'Phrase does not match'
      }
    };
  },
  methods: {
    showConfirmDialog() {
      this.userInput = '';
      this.error = '';
      this.dialogVisible = true;
    },
    
    cancelReset() {
      this.dialogVisible = false;
      this.userInput = '';
      this.error = '';
    },
    
    async confirmReset() {
      if (this.userInput !== this.confirmPhrase) {
        this.error = 'Phrase does not match. Reset cancelled.';
        return;
      }
      
      this.loading = true;
      try {
        // Send reset request to the API
        const { data } = await api.get(`/v2/reset?confirmation=${encodeURIComponent(this.userInput)}`);
        
        // Show success message
        this.resetSuccess = true;
        this.resetMessage = data.message || 'System has been reset successfully.';
        
        // Close the confirmation dialog and show the result dialog
        this.dialogVisible = false;
        this.resultDialogVisible = true;
      } catch (error) {
        console.error('Failed to reset system', error);
        
        // Show error message
        this.resetSuccess = false;
        this.resetMessage = error.response?.data?.message || 'Failed to reset system. Please try again or contact support.';
        
        // Close the confirmation dialog and show the result dialog
        this.dialogVisible = false;
        this.resultDialogVisible = true;
      } finally {
        this.loading = false;
        this.userInput = '';
      }
    }
  }
};
</script>

<style scoped>
.reset-container {
  margin-top: 3rem;
}
</style>